import { createTheme } from '@mui/material/styles';
import { brand, darkPalette, lightPalette } from './tokens';
import { LAYOUT } from './layout';

/** `theme.vars` çıktısında `X.main` bir `var(--tfw-...)` dizesi olduğundan `alpha()`
 *  kullanılamaz; yarı saydam marka tonları için kanal token'ı + `rgb(... / a)` gerekir. */
const primaryTint = (t: { vars: { palette: { primary: { mainChannel: string } } } }, a: number) =>
  `rgba(${t.vars.palette.primary.mainChannel} / ${a})`;

/** Tek yumuşak gölge seti — kartlar düz (kenarlık taşır), gölge yalnız overlay'lerde. */
const SOFT_SHADOW = '0 1px 2px rgba(16, 21, 38, 0.05), 0 1px 3px rgba(16, 21, 38, 0.04)';

/** Vivid royal blue — paired with a warm coral-orange accent, a classic energetic
 *  airline/travel combo (THY, Pegasus, ...). The palette itself now lives in
 *  `tokens.ts` (light + dark); this file assembles the theme, keeps the shared
 *  shape/typography, and holds the MUI component overrides. `alpha(...)` tints below
 *  are computed from the brand constants so they stay internally consistent.
 *
 *  Dark mode: `cssVariables` + `colorSchemes` — MUI generates a CSS-variable stylesheet
 *  and `ThemeProvider` switches schemes via the `class` selector on `<html>`. The
 *  `theme` export name is kept stable (App.tsx + test/renderWithProviders.tsx import it). */
const SECONDARY = brand.coral;

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'class', cssVarPrefix: 'tfw' },
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: ['"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    // Modüler ölçek (~1.2) + tutarlı satır yüksekliği. Sayfa başlıkları `h5`,
    // bölüm başlıkları `h6`/`subtitle1` — detay sayfalarında hiyerarşi için ayrı.
    h1: { fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.2, letterSpacing: -0.5 },
    h2: { fontWeight: 800, fontSize: '1.875rem', lineHeight: 1.22, letterSpacing: -0.4 },
    h3: { fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.25, letterSpacing: -0.3 },
    h4: { fontWeight: 800, fontSize: '1.3125rem', lineHeight: 1.3, letterSpacing: -0.3 },
    h5: { fontWeight: 700, fontSize: '1.1875rem', lineHeight: 1.35, letterSpacing: -0.2 },
    h6: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.4, letterSpacing: -0.1 },
    subtitle1: { fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontWeight: 600, lineHeight: 1.45 },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.55 },
    caption: { lineHeight: 1.45 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: LAYOUT.controlRadius, paddingInline: 16 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: t.vars.palette.primary.main,
          backgroundImage: 'none',
          boxShadow: `0 1px 0 ${t.vars.palette.divider}`,
          color: t.vars.palette.primary.contrastText,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          backgroundColor: t.vars.palette.background.paper,
          borderRight: `1px solid ${t.vars.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderLeft: '3px solid transparent',
          transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
          '&.Mui-selected': {
            backgroundColor: primaryTint(t, 0.1),
            borderLeftColor: SECONDARY,
            color: t.vars.palette.primary.main,
            fontWeight: 600,
            '& .MuiListItemIcon-root': { color: t.vars.palette.primary.main },
            '&:hover': { backgroundColor: primaryTint(t, 0.16) },
          },
          '&:hover': { backgroundColor: t.vars.palette.action.hover },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme: t }) => ({ minWidth: 40, color: t.vars.palette.text.secondary }),
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: LAYOUT.cardRadius,
          borderColor: t.vars.palette.divider,
          boxShadow: SOFT_SHADOW,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: ({ theme: t }) => ({ borderColor: t.vars.palette.divider }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme: t }) => ({
          backgroundColor: t.vars.palette.surfaceSunken,
          color: t.vars.palette.text.primary,
          fontWeight: 700,
          fontSize: '0.8125rem',
          borderBottom: `1px solid ${t.vars.palette.divider}`,
          whiteSpace: 'nowrap',
        }),
        root: ({ theme: t }) => ({
          borderBottom: `1px solid ${t.vars.palette.divider}`,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          '&:hover': { backgroundColor: t.vars.palette.action.hover },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: LAYOUT.controlRadius },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme: t }) => ({
          height: 3,
          borderRadius: 3,
          backgroundColor: t.vars.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
  },
});
